import React from 'react';

/**
 * 🏷️ ICON BADGE - Composant badge avec icône
 * ===========================================
 * 
 * Composant pour afficher des badges avec icônes,
 * parfait pour les statistiques, features, etc.
 */

const IconBadge = ({
  icon,
  iconComponent: IconComponent,
  title,
  description,
  value,
  unit = "",
  color = "blue",
  variant = "default",
  size = "md",
  className = "",
  animated = false
}) => {
  const colorClasses = {
    blue: {
      background: "bg-blue-50 border-blue-200",
      icon: "text-blue-600",
      title: "text-blue-900",
      description: "text-blue-700",
      value: "text-blue-800"
    },
    green: {
      background: "bg-green-50 border-green-200",
      icon: "text-green-600",
      title: "text-green-900",
      description: "text-green-700",
      value: "text-green-800"
    },
    purple: {
      background: "bg-purple-50 border-purple-200",
      icon: "text-purple-600",
      title: "text-purple-900",
      description: "text-purple-700",
      value: "text-purple-800"
    },
    orange: {
      background: "bg-orange-50 border-orange-200",
      icon: "text-orange-600",
      title: "text-orange-900",
      description: "text-orange-700",
      value: "text-orange-800"
    },
    red: {
      background: "bg-red-50 border-red-200",
      icon: "text-red-600",
      title: "text-red-900",
      description: "text-red-700",
      value: "text-red-800"
    }
  };

  const sizeClasses = {
    sm: {
      container: "p-4",
      icon: "w-6 h-6 mb-2",
      title: "text-sm font-semibold",
      description: "text-xs",
      value: "text-lg font-bold"
    },
    md: {
      container: "p-6",
      icon: "w-8 h-8 mb-3",
      title: "text-base font-semibold",
      description: "text-sm",
      value: "text-xl font-bold"
    },
    lg: {
      container: "p-8",
      icon: "w-10 h-10 mb-4",
      title: "text-lg font-semibold",
      description: "text-base",
      value: "text-2xl font-bold"
    }
  };

  const variantClasses = {
    default: "border-2 rounded-xl",
    solid: "rounded-xl shadow-lg",
    minimal: "border-l-4",
    floating: "rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
  };

  const colors = colorClasses[color] || colorClasses.blue;
  const sizes = sizeClasses[size] || sizeClasses.md;

  const containerClasses = `
    ${colors.background} 
    ${variantClasses[variant]} 
    ${sizes.container} 
    ${animated ? 'transform hover:scale-105 transition-transform duration-300' : ''}
    ${className}
  `.trim();

  return (
    <div className={containerClasses}>
      {/* Icône */}
      <div className={`${colors.icon} ${sizes.icon} flex items-center justify-center`}>
        {IconComponent ? (
          <IconComponent className="w-full h-full" />
        ) : icon ? (
          <span className="text-current">{icon}</span>
        ) : (
          <div className="w-full h-full bg-current rounded opacity-20" />
        )}
      </div>

      {/* Valeur principale */}
      {value && (
        <div className={`${colors.value} ${sizes.value} mb-1`}>
          {value}{unit && <span className="text-sm font-normal ml-1">{unit}</span>}
        </div>
      )}

      {/* Titre */}
      {title && (
        <h3 className={`${colors.title} ${sizes.title} mb-1`}>
          {title}
        </h3>
      )}

      {/* Description */}
      {description && (
        <p className={`${colors.description} ${sizes.description} leading-relaxed`}>
          {description}
        </p>
      )}
    </div>
  );
};

export default IconBadge;